import {
	type ProjectNodeInputs,
	parseProjectNodeOutput,
} from "@oblique/shared/runtime"
import { mutators } from "@oblique/shared/zero"
import { useZero } from "@rocicorp/zero/react"
import {
	applyEdgeChanges,
	applyNodeChanges,
	type Edge,
	type EdgeChange,
	type Node,
	type NodeChange,
	type OnEdgesChange,
	type OnNodesChange,
	useEdgesState,
	useNodesState,
} from "@xyflow/react"
import { useCallback, useEffect, useMemo } from "react"
import { useParams } from "react-router"
import { imageNodeOutput } from "@/components/project/nodes/image-node"
import type { ProjectNodeData } from "@/types/project"
import type { ProjectByIdResult } from "@/types/zero"

type ZeroNode = {
	code: string
	id: string
	inputs: unknown
	label: string
	outputs: unknown
	type: "python" | "react"
	position?: unknown
	metadata?: unknown
	geometries?: Array<{ serialized_data?: string | null }>
}

type ZeroEdge = {
	id: string
	source_node_id: string
	target_node_id: string
	source_handle?: string | null
	target_handle?: string | null
}

const DEFAULT_POSITION = { x: 0, y: 0 }

function normalizeRecord(value: unknown): Record<string, unknown> {
	if (!value) return Object.create(null)

	if (typeof value === "string") {
		try {
			const parsed = JSON.parse(value) as unknown
			if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
				return parsed as Record<string, unknown>
			}
		} catch {
			return Object.create(null)
		}
	}

	if (typeof value === "object" && !Array.isArray(value)) {
		return value as Record<string, unknown>
	}

	return Object.create(null)
}

function normalizePosition(value: unknown): { x: number; y: number } {
	if (!value) return DEFAULT_POSITION
	if (typeof value === "string") {
		try {
			const parsed = JSON.parse(value) as unknown
			if (
				parsed &&
				typeof parsed === "object" &&
				"x" in parsed &&
				"y" in parsed
			) {
				const { x, y } = parsed as { x: number; y: number }
				return { x: x ?? 0, y: y ?? 0 }
			}
		} catch {
			return DEFAULT_POSITION
		}
	}

	if (typeof value === "object" && value !== null) {
		const { x, y } = value as { x?: number; y?: number }
		return { x: x ?? 0, y: y ?? 0 }
	}

	return DEFAULT_POSITION
}

function normalizeHandle(value: unknown): string | undefined {
	if (typeof value === "string" && value.length > 0) return value
	return undefined
}

function shallowEqualRecord(
	a: Record<string, unknown>,
	b: Record<string, unknown>,
): boolean {
	if (a === b) return true
	const aKeys = Object.keys(a)
	const bKeys = Object.keys(b)
	if (aKeys.length !== bKeys.length) return false
	for (const key of aKeys) {
		if (!Object.hasOwn(b, key)) return false
		if (a[key] !== b[key]) return false
	}
	return true
}

function nodeDataEqual(a: ProjectNodeData, b: ProjectNodeData): boolean {
	if (a.id !== b.id) return false
	if (a.label !== b.label) return false
	if (a.type !== b.type) return false
	if (a.code !== b.code) return false
	if (JSON.stringify(a.outputs) !== JSON.stringify(b.outputs)) return false
	if (JSON.stringify(a.inputs) !== JSON.stringify(b.inputs)) return false
	if (a.status !== b.status) return false
	if (a.lastRequestId !== b.lastRequestId) return false
	if (a.lastRunAt !== b.lastRunAt) return false
	if (JSON.stringify(a.result) !== JSON.stringify(b.result)) return false
	if (a.error !== b.error) return false
	if (!shallowEqualRecord(a.metadata, b.metadata)) return false
	return true
}

function positionsEqual(
	a: { x: number; y: number },
	b: { x: number; y: number },
): boolean {
	return a.x === b.x && a.y === b.y
}

const latestNodeOutput = (node: ZeroNode) => {
	const serialized = node.geometries?.[0]?.serialized_data
	if (typeof serialized !== "string") return undefined
	return parseProjectNodeOutput(serialized)
}

function buildNodeDataFromZero(zeroNode: ZeroNode): ProjectNodeData {
	const metadata = normalizeRecord(zeroNode.metadata)
	const inputs = normalizeRecord(zeroNode.inputs) as ProjectNodeInputs
	const persistedResult = latestNodeOutput(zeroNode)
	const staticImageOutput = imageNodeOutput(metadata, inputs)
	const hasResult =
		Object.hasOwn(metadata, "result") ||
		persistedResult !== undefined ||
		staticImageOutput !== undefined

	return {
		code: zeroNode.code,
		id: zeroNode.id,
		inputs,
		label: zeroNode.label,
		outputs: Array.isArray(zeroNode.outputs)
			? zeroNode.outputs.filter(
					(value): value is string => typeof value === "string",
				)
			: [],
		type: zeroNode.type,
		metadata,
		status: (metadata.status as ProjectNodeData["status"]) ?? "idle",
		lastRequestId: metadata.lastRequestId as string | undefined,
		lastRunAt: metadata.lastRunAt as string | undefined,
		...(hasResult && {
			result:
				persistedResult !== undefined
					? persistedResult
					: (staticImageOutput ?? metadata.result),
		}),
		error: metadata.error as string | null | undefined,
	} as ProjectNodeData
}

function buildNodeFromZero(zeroNode: ZeroNode): Node<ProjectNodeData> {
	const data = buildNodeDataFromZero(zeroNode)
	return {
		id: zeroNode.id,
		position: normalizePosition(zeroNode.position),
		data,
		type: data.type,
	}
}

function mergeZeroNodes(
	current: Node<ProjectNodeData>[],
	zeroNodes: ZeroNode[],
): Node<ProjectNodeData>[] {
	const zeroById = new Map<string, ZeroNode>()
	for (const node of zeroNodes) {
		zeroById.set(node.id, node)
	}

	const next: Node<ProjectNodeData>[] = []

	for (const existing of current) {
		const zeroNode = zeroById.get(existing.id)
		if (!zeroNode) continue

		const nextData = buildNodeDataFromZero(zeroNode)
		const nextType = nextData.type
		const zeroPosition = normalizePosition(zeroNode.position)
		const nextPosition = existing.dragging ? existing.position : zeroPosition

		const dataUnchanged = nodeDataEqual(existing.data, nextData)
		const typeUnchanged = existing.type === nextType
		const positionUnchanged = positionsEqual(existing.position, nextPosition)

		if (dataUnchanged && typeUnchanged && positionUnchanged) {
			next.push(existing)
		} else {
			next.push({
				...existing,
				position: nextPosition,
				data: nextData,
				type: nextType,
			})
		}

		zeroById.delete(existing.id)
	}

	for (const zeroNode of zeroById.values()) {
		next.push(buildNodeFromZero(zeroNode))
	}

	return next
}

function mergeZeroEdges(current: Edge[], zeroEdges: ZeroEdge[]): Edge[] {
	const zeroById = new Map<string, ZeroEdge>()
	for (const edge of zeroEdges) {
		zeroById.set(edge.id, edge)
	}

	const next: Edge[] = []

	for (const existing of current) {
		const zeroEdge = zeroById.get(existing.id)
		if (!zeroEdge) continue

		const nextSource = zeroEdge.source_node_id
		const nextTarget = zeroEdge.target_node_id
		const nextSourceHandle = normalizeHandle(zeroEdge.source_handle)
		const nextTargetHandle = normalizeHandle(zeroEdge.target_handle)

		const unchanged =
			existing.source === nextSource &&
			existing.target === nextTarget &&
			existing.sourceHandle === nextSourceHandle &&
			existing.targetHandle === nextTargetHandle

		if (unchanged) {
			next.push(existing)
		} else {
			next.push({
				...existing,
				source: nextSource,
				target: nextTarget,
				sourceHandle: nextSourceHandle,
				targetHandle: nextTargetHandle,
			})
		}

		zeroById.delete(existing.id)
	}

	for (const zeroEdge of zeroById.values()) {
		next.push({
			id: zeroEdge.id,
			source: zeroEdge.source_node_id,
			target: zeroEdge.target_node_id,
			sourceHandle: normalizeHandle(zeroEdge.source_handle),
			targetHandle: normalizeHandle(zeroEdge.target_handle),
			type: "oblique",
		})
	}

	return next
}

/**
 * Hybrid hook that merges Zero data into local React Flow state.
 * Zero is canonical for structure and persisted fields, while local
 * state preserves transient UI (dragging, selection).
 */
export function useZeroFlowState(project: ProjectByIdResult) {
	const params = useParams<{ projectId: string }>()
	const projectId = params?.projectId ?? ""
	const z = useZero()

	const [nodes, setNodes] = useNodesState<Node<ProjectNodeData>>([])
	const [edges, setEdges] = useEdgesState<Edge>([])

	useEffect(() => {
		const projectNodes = (project?.project_nodes ?? []) as unknown as ZeroNode[]
		setNodes((current) => mergeZeroNodes(current, projectNodes))
	}, [project?.project_nodes, setNodes])

	useEffect(() => {
		const projectEdges = (project?.project_edges ?? []) as unknown as ZeroEdge[]
		setEdges((current) => mergeZeroEdges(current, projectEdges))
	}, [project?.project_edges, setEdges])

	// Route node changes to Zero mutations
	// Only commits final changes - React Flow handles transient drag state internally
	const onNodesChange: OnNodesChange<Node<ProjectNodeData>> = useCallback(
		(changes: NodeChange<Node<ProjectNodeData>>[]) => {
			setNodes((current) =>
				applyNodeChanges<Node<ProjectNodeData>>(changes, current),
			)

			for (const change of changes) {
				if (change.type === "position" && change.position) {
					if (!change.dragging) {
						// Final position after drag - commit to Zero
						z.mutate(
							mutators.nodes.update({
								project_id: projectId,
								node_id: change.id,
								position: change.position,
							}),
						)
					}
				}
				if (change.type === "remove") {
					z.mutate(
						mutators.nodes.delete({
							project_id: projectId,
							node_id: change.id,
						}),
					)
				}
				// 'select' and 'dimensions' changes are handled by React Flow internally
				// 'add' changes are handled separately via node creation hooks
			}
		},
		[z, projectId, setNodes],
	)

	// Route edge changes to Zero mutations
	const onEdgesChange: OnEdgesChange<Edge> = useCallback(
		(changes: EdgeChange<Edge>[]) => {
			setEdges((current) => applyEdgeChanges(changes, current))

			for (const change of changes) {
				if (change.type === "remove") {
					z.mutate(
						mutators.edges.delete({
							project_id: projectId,
							edge_id: change.id,
						}),
					)
				}
				// 'add' changes are handled via onConnect in useEdgeManagement
				// 'select' changes are handled by React Flow internally
			}
		},
		[z, projectId, setEdges],
	)

	return {
		nodes,
		edges,
		onNodesChange,
		onEdgesChange,
	}
}
